import { useEffect, useRef, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sparkles, Send, Download, Loader2 } from "lucide-react";

export default function AIAssistant() {
  const [brief, setBrief] = useState({ name: "", email: "", project_idea: "" });
  const [briefId, setBriefId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [finalText, setFinalText] = useState("");
  const scroller = useRef(null);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages]);

  const start = async (e) => {
    e.preventDefault();
    if (!brief.project_idea.trim()) { toast.error("Décrivez votre projet en quelques mots"); return; }
    setLoading(true);
    try {
      const r = await api.post("/ai/brief/start", brief);
      setBriefId(r.data.brief_id);
      setMessages(r.data.messages);
    } catch {
      toast.error("L'assistant n'a pas pu démarrer. Réessayez.");
    } finally { setLoading(false); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const userMsg = input;
    setMessages((m) => [...m, { role: "user", text: userMsg }]);
    setInput("");
    setLoading(true);
    try {
      const r = await api.post(`/ai/brief/${briefId}/message`, { message: userMsg });
      setMessages(r.data.messages);
    } catch {
      toast.error("Erreur côté assistant.");
    } finally { setLoading(false); }
  };

  const finalize = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/ai/brief/${briefId}/finalize`);
      setFinalText(r.data.final_brief);
      toast.success("Cahier des charges généré !");
    } catch {
      toast.error("Impossible de finaliser.");
    } finally { setLoading(false); }
  };

  const download = () => {
    const blob = new Blob([finalText], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `cahier-des-charges-besson.md`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Header />
      <main className="pt-36 sm:pt-40 pb-20 bg-[#FAFAFA] min-h-screen" data-testid="ai-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0A1128] text-white text-xs font-bold tracking-wider uppercase">
              <Sparkles size={14} className="text-[#67E8F9]" /> Assistant IA
            </div>
            <h1 className="display mt-4 text-3xl sm:text-5xl text-[#0A1128]">Votre cahier des charges, guidé.</h1>
            <p className="mt-3 text-[#475569]">L'IA vous interroge pour cadrer votre projet. Vous obtenez un livrable structuré, prêt à partager.</p>
          </div>

          {!briefId ? (
            <form onSubmit={start} className="mt-10 bg-white rounded-3xl border border-black/8 p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="brief-start-form">
              <div>
                <Label>Votre prénom</Label>
                <Input data-testid="brief-name" value={brief.name} onChange={(e) => setBrief({ ...brief, name: e.target.value })} />
              </div>
              <div>
                <Label>Email</Label>
                <Input data-testid="brief-email" type="email" value={brief.email} onChange={(e) => setBrief({ ...brief, email: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <Label>Décrivez votre projet *</Label>
                <Textarea data-testid="brief-idea" rows={4} value={brief.project_idea} onChange={(e) => setBrief({ ...brief, project_idea: e.target.value })} placeholder="Ex: une app B2B pour gérer mes leads, avec relances auto et tableau de bord..." required />
              </div>
              <div className="sm:col-span-2 flex justify-end">
                <Button type="submit" disabled={loading} data-testid="brief-start-btn" className="bg-[#0A1128] hover:bg-[#14213d] text-white rounded-full px-7 py-6">
                  {loading ? <><Loader2 className="mr-2 animate-spin" size={16} /> Lancement...</> : <><Sparkles size={16} className="mr-2" /> Démarrer l'assistant</>}
                </Button>
              </div>
            </form>
          ) : (
            <div className="mt-10 bg-white rounded-3xl border border-black/8 overflow-hidden">
              <div ref={scroller} className="px-5 py-6 max-h-[60vh] overflow-y-auto space-y-3" data-testid="brief-chat">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`px-4 py-2.5 rounded-2xl text-sm max-w-[80%] whitespace-pre-wrap leading-relaxed ${
                      m.role === "user" ? "bg-[#0A1128] text-white rounded-tr-sm" : "bg-[#FAFAFA] border border-black/5 text-[#0A1128] rounded-tl-sm"
                    }`}>
                      {m.text}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="px-4 py-2.5 rounded-2xl bg-[#FAFAFA] border border-black/5 text-[#64748B] text-sm">
                      <Loader2 size={14} className="inline animate-spin mr-2" /> L'assistant réfléchit...
                    </div>
                  </div>
                )}
              </div>

              {!finalText && (
                <form onSubmit={send} className="border-t border-black/8 p-4 flex gap-2" data-testid="brief-form">
                  <Input
                    data-testid="brief-input"
                    placeholder="Votre réponse..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    disabled={loading}
                  />
                  <Button type="submit" disabled={loading || !input.trim()} data-testid="brief-send" className="bg-[#06B6D4] hover:bg-[#0891b2] text-[#0A1128]">
                    <Send size={16} />
                  </Button>
                  <Button type="button" onClick={finalize} disabled={loading || messages.length < 4} data-testid="brief-finalize" className="bg-[#FFDCA8] hover:bg-[#FFD08A] text-[#0A1128]">
                    Générer le brief
                  </Button>
                </form>
              )}

              {finalText && (
                <div className="border-t border-black/8 p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="display text-xl font-bold text-[#0A1128]">Cahier des charges</h3>
                    <Button onClick={download} data-testid="brief-download" className="bg-[#0A1128] hover:bg-[#14213d] text-white">
                      <Download size={16} className="mr-2" /> Télécharger
                    </Button>
                  </div>
                  <pre className="whitespace-pre-wrap bg-[#FAFAFA] border border-black/8 rounded-xl p-4 text-sm text-[#0A1128] max-h-[50vh] overflow-y-auto">{finalText}</pre>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
